import axios from 'axios'
import React, { useCallback, useState } from 'react'
import { FileWithPath } from 'react-dropzone'
import { FileInfo, useGetUploadUrlLazyQuery } from '../graphql/generated'
import { captureException } from '../utils/Sentry'

export interface UploadInfo {
  loaded: number
  total: number
}

export interface UploadedFileInfo extends FileInfo {
  file: FileWithPath
}

export interface FileUploadState {
  files: Set<FileWithPath>
  uploadProgress: UploadInfo[]
  addFilesToMap: (newFiles: readonly FileWithPath[]) => void
  removeFileFromMap: (fileToRemove: FileWithPath) => void
  setUploadProgress: React.Dispatch<React.SetStateAction<UploadInfo[]>>
  removeFiles: (files: readonly FileWithPath[]) => void
  computeOverallProgress: () => number
  uploadFile: (file: FileWithPath, index: number) => Promise<UploadedFileInfo>
}

export function useFileUploadState(): FileUploadState {
  const [files, setFiles] = useState<Set<FileWithPath>>(new Set())
  const [uploadProgress, setUploadProgress] = useState<UploadInfo[]>([])

  const [getUploadUrl] = useGetUploadUrlLazyQuery({ fetchPolicy: 'no-cache' })

  const addFilesToMap = useCallback((newFiles: readonly FileWithPath[]) => {
    setFiles((current) => {
      newFiles.forEach((file) => current.add(file))
      return new Set(current) // Create a new map for React to re-render
    })
  }, [])

  const removeFileFromMap = useCallback((fileToRemove: FileWithPath) => {
    setFiles((current) => {
      current.delete(fileToRemove)
      return new Set(current) // Create a new map for React to re-render
    })
  }, [])

  const removeFiles = useCallback((toRemove: readonly FileWithPath[]) => {
    setFiles((oldSet) => {
      const toRemoveSet = new Set(toRemove)
      const newSet: Set<FileWithPath> = new Set()
      oldSet.forEach((file) => {
        if (!toRemoveSet.has(file)) {
          newSet.add(file)
        }
      })
      return newSet
    })
  }, [])

  const computeOverallProgress = useCallback(() => {
    if (uploadProgress.length === 0) return 0
    const [total, loaded] = uploadProgress.reduce(
      (agg, fileInfo) => [
        agg[0] + (fileInfo.total || 0),
        agg[1] + (fileInfo.loaded || 0),
      ],
      [0, 0],
    )
    if (total === 0) return 0 // Avoid division by zero
    return Math.round((loaded * 100) / total)
  }, [uploadProgress])

  const uploadFile = async (
    file: FileWithPath,
    index: number,
  ): Promise<UploadedFileInfo> => {
    const uploadUrlResult = await getUploadUrl({
      // the variable is only here to distinguiche between concurent query.
      // Some problem were observed in tests when doing concurent call without variables.
      // And as we can't be sure that it is only a problem with the mock provider we are seting variables that won't get used
      // The goal is only for the variable to be unique so use the file path if it exist and fall back to the name otherwise
      variables: { fileName: file.path ?? file.name },
    })
    if (uploadUrlResult.error) {
      captureException(uploadUrlResult.error)
      throw new Error('error while getting the upload url')
    }
    const fileInfo = uploadUrlResult.data?.getUploadUrl
    if (!fileInfo) {
      captureException(new Error('error: getUploadUrl returned no fileInfo'))
      throw new Error('error while getting the upload url')
    }

    await axios.put(fileInfo.signedUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
      onUploadProgress: (progressEvent) => {
        const total = progressEvent.total
        if (total) {
          setUploadProgress((prevState) => {
            const newState = [...prevState]
            newState[index] = {
              loaded: progressEvent.loaded,
              total,
            }
            return newState
          })
        }
      },
    })

    return { ...fileInfo, file }
  }

  return {
    files,
    uploadProgress,
    addFilesToMap,
    removeFileFromMap,
    setUploadProgress,
    removeFiles,
    computeOverallProgress,
    uploadFile,
  }
}
